import {ChangeDetectionStrategy, Component, Input, OnInit} from "@angular/core";
import {Order, OrderStatusEnum, Product, ProductPricing, StandardName} from "../../../store/state/order.state";
import {ChangeSet} from "../history/order-history.model";
import {IntermediatePriceAdjustment, PriceAdjustmentsConfig} from "./pricing-adjustments/pricing-adjustments.component";
import {AuthenticationService} from "../../../authentication/authentication.service";
import {Actions, ofType} from "@ngrx/effects";
import {ReviewerActions} from "../../../store/actions/reviewer.actions";
import {ConfigurationProvider} from "../../../configuration/configuration.provider";
import {map} from "rxjs/operators";

@Component({
    selector: 'order-pricing',
    templateUrl: './order-pricing.template.html',
    styleUrls: ['./order-pricing.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OrderPricingComponent implements OnInit {

	@Input() order: Order;
	@Input() products: Product[];
	@Input() orderUuid: string;
	@Input() orderCorrelationId: string;
	@Input() includeOffsiteDays: boolean;
	@Input() allowPdfDownload: boolean;
	@Input() disablePdfDownloadButton: boolean = false;
	@Input() showDownloadButtonText: boolean = true;
	@Input() changeSet: ChangeSet; // optional
	@Input() withPricingAdjustmentsEditMode = false;
	@Input() showDailyRate: boolean = false;
	@Input() showSummary: boolean = true;
	@Input() visibleOnInit: boolean = true;
	@Input() intermediatePriceAdjustments: IntermediatePriceAdjustment[];
	@Input() priceAdjustmentsConfig: PriceAdjustmentsConfig;
	@Input() hackForCertificationFee: boolean = false; // https://jira.dekra-glass.com/browse/UMB-4805 bug#8
	@Input() collapsable: boolean = true;
	@Input() tooltipForDisableDownloadButton: string = null;
	@Input() show3YearsTotalForClient!: boolean;

	isPricingChanged: boolean;
	isClient: boolean = false;
	private pricingVisibility: Map<StandardName, boolean>;

	constructor(private authService: AuthenticationService, private actions$: Actions) {
	}

	ngOnInit(): void {
		this.isClient = this.authService.isLoggedUserClient(true);
		let productVisibility: ReadonlyArray<any> = Object.getOwnPropertyNames(StandardName).map((standard: string) => [standard, this.visibleOnInit]);
		this.pricingVisibility = new Map(productVisibility);

		this.actions$.pipe(ofType(ReviewerActions.CANCEL_CHANGE_PROPOSAL))
			.subscribe((payload) => {
				this.isPricingChanged = payload.order.isPricingChanged
			});
	}

	isVisible(product: Product): boolean {
		return this.pricingVisibility.has(product.standard.name) && this.pricingVisibility.get(product.standard.name);
	}

	toggleVisibility(product: Product) {
		if (this.pricingVisibility.has(product.standard.name)) {
			this.pricingVisibility.set(product.standard.name, !this.pricingVisibility.get(product.standard.name));
		}
	}

	pricings(): ProductPricing[] {
		return this.products.map(product => product.pricing);
	}

	trackByIndex(index) {
		return index;
	}

	isTotalPriceChangedFor(product: Product): boolean {
		const productChange = !!this.changeSet && this.changeSet.changes.productChanges[product.standard.name];
		if (productChange == null) {
			return false;
		}

		return !!productChange.totalPrice;
	}

	getAdjustmentsForProduct(product: Product): IntermediatePriceAdjustment[] {
		return this.intermediatePriceAdjustments.filter(adj => adj.standards.includes(product.standard.name));
	}

	showPricingChangedInfo(): boolean {
		if (this.order.isSalesLeadArchived) {
			return false;
		}
		return this.order.isPricingChanged && (
			[
				OrderStatusEnum.LEAD, OrderStatusEnum.NEW, OrderStatusEnum.SALES_LEAD_DRAFT, OrderStatusEnum.UNDER_APPLICATION_REVIEW,
				OrderStatusEnum.REVIEW_CHANGE_DRAFT, OrderStatusEnum.REVIEW_IN_PROGRESS, OrderStatusEnum.ORDER_CONFIRMATION
			]
				.includes(this.order.status));
	}

	isEmployeeLogged(): boolean {
		return this.authService.isDekraEmployeeLogged();
	}
}
